import { sendEstimatorLeadEmails } from "@/lib/email/leadEmails.functions";
import { deliverLeadEmails } from "@/lib/email/deliverLeadEmails";
import { useState } from "react";
import { z } from "zod";
import { ArrowRight, Loader2, CheckCircle2, Phone, MessageCircle, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useFunnelTracking } from "@/hooks/useFunnelTracking";
import PreSubmitTrust from "@/components/PreSubmitTrust";
import { formatUSPhone } from "@/lib/phone";
import { getChannelResponseLine } from "@/lib/businessHours";
import WhatHappensNext from "@/components/WhatHappensNext";
import { attributionColumns } from "@/lib/attribution";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name").max(100),
  phone: z
    .string()
    .trim()
    .min(7, "Please enter a valid phone number")
    .max(30)
    .regex(/^[+()\-.\s\d]+$/, "Please enter a valid phone number"),
  zip: z.string().trim().max(10).optional().or(z.literal("")),
  feet: z.number().int().nonnegative().optional(),
});

type Variant = "horizontal" | "stacked" | "inline";

interface QuickQuoteFormProps {
  /** Source label saved on the lead row (e.g. 'hero-mini', 'service:dog-fence', 'scroll-intent'). */
  source: string;
  /** Display variant. */
  variant?: Variant;
  /** Optional pre-fill for zip (e.g. on a location page). */
  defaultZip?: string;
  /** Optional eyebrow / heading shown above the fields. */
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  /** Compact rendering — no eyebrow, smaller heading. */
  compact?: boolean;
  /** Show the linear-feet field (default false on small variants). */
  showFeet?: boolean;
  /** Background tone. */
  tone?: "white" | "offwhite" | "primary-soft";
  className?: string;
  /** Optional id for anchor scrolling. */
  id?: string;
  /** Show the small sub-line under the heading (accepted for call-site compat). */
  sub?: boolean;
}

const QuickQuoteForm = ({
  source,
  variant = "horizontal",
  defaultZip = "",
  eyebrow,
  heading = "Get your free quote in 24 hours",
  subheading = "No pressure. No door-to-door. Just a fast, fair number.",
  compact = false,
  showFeet = false,
  tone = "white",
  className = "",
  id,
}: QuickQuoteFormProps) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState(defaultZip);
  const [feet, setFeet] = useState<string>("");
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [touched, setTouched] = useState<{ name?: boolean; phone?: boolean }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [preferText, setPreferText] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  // Optional fields (zip / feet) collapse into an expander on mobile to keep
  // the visible field count down — biggest mobile-conversion lever.
  const [showOptional, setShowOptional] = useState(false);
  const { toast } = useToast();
  const funnel = useFunnelTracking("quick_quote", source);

  const nameValid = schema.shape.name.safeParse(name).success;
  const phoneValid = schema.shape.phone.safeParse(phone).success;

  // Show inline error only after the user has interacted (blurred) the field
  // OR after a failed submit attempt populated `errors`.
  const showNameError = !!errors.name && (touched.name ?? false);
  const showPhoneError = !!errors.phone && (touched.phone ?? false);

  const validateField = (field: "name" | "phone", value: string) => {
    const result = schema.shape[field].safeParse(value);
    setErrors((e) => ({
      ...e,
      [field]: result.success ? undefined : result.error.issues[0]?.message,
    }));
  };

  const submit = async (asText: boolean) => {
    const feetNum = feet ? parseInt(feet, 10) : undefined;
    funnel.submitAttempt({ prefer_text: asText, has_zip: Boolean(zip), has_feet: feetNum != null, sms_consent: smsConsent });
    // SMS path requires explicit consent (TCPA-aligned).
    if (asText && !smsConsent) {
      setConsentError(true);
      funnel.submitError("validation", { fields: ["sms_consent"], prefer_text: asText });
      document.getElementById(`qq-sms-consent-${source}`)?.focus();
      return;
    }
    const parsed = schema.safeParse({ name, phone, zip, feet: feetNum });
    if (!parsed.success) {
      const fe: { name?: string; phone?: string } = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as "name" | "phone";
        if ((k === "name" || k === "phone") && !fe[k]) fe[k] = issue.message;
      }
      setErrors(fe);
      // Make sure errors render immediately even if user never blurred a field.
      setTouched({ name: true, phone: true });
      funnel.validationErrorBatch(fe);
      funnel.submitError("validation", { fields: Object.keys(fe), prefer_text: asText });
      // Move keyboard focus to the first invalid field — biggest a11y/UX win.
      const firstBad = fe.name ? "name" : fe.phone ? "phone" : null;
      if (firstBad) {
        const el = document.getElementById(`qq-${firstBad}-${source}`);
        el?.focus({ preventScroll: false });
      }
      return;
    }
    setErrors({});
    setConsentError(false);
    setSubmitting(true);
    setPreferText(asText);

    const id = crypto.randomUUID();
    try {
      const { error } = await supabase.from("estimator_leads").insert({
        ...attributionColumns(),
        id,
        name: parsed.data.name,
        phone: parsed.data.phone,
        email: null,
        linear_feet: feetNum ?? 0,
        gates: 0,
        estimate_low: 0,
        estimate_high: 0,
        source,
        notes: [
          parsed.data.zip ? `Zip: ${parsed.data.zip}` : null,
          asText ? "Prefers text" : null,
          smsConsent ? `SMS consent: yes (${new Date().toISOString()})` : "SMS consent: no",
        ]
          .filter(Boolean)
          .join(" · ") || null,
      });
      if (error) throw error;

      void deliverLeadEmails({
        context: "quick-quote",
        leadId: id,
        send: () => sendEstimatorLeadEmails({
        data: {
          leadId: id,
          keyPrefix: "quick-quote",
          name: parsed.data.name,
          phone: parsed.data.phone,
          linearFeet: feetNum ?? 0,
          gates: 0,
          estimateLow: 0,
          estimateHigh: 0,
          source,
          zip: parsed.data.zip || undefined,
          preferText: asText,
          smsConsent: smsConsent,
          sendCustomerCopy: false,
        },
      })});

      void import("@/lib/analytics").then(({ conversions }) =>
        conversions.quickQuoteSubmit(source, id),
      );
      void import("@/lib/experimentConvert").then(({ recordExperimentConversion }) =>
        recordExperimentConversion("quick_quote_submit", { source }),
      );
      funnel.submitSuccess({ prefer_text: asText, linear_feet: feetNum, has_zip: Boolean(zip) });

      setSubmitted(true);
      toast({
        title: "Got it — we'll be in touch",
        description: getChannelResponseLine(asText ? "text" : "call"),
      });
    } catch (err) {
      console.error("Quick quote submit failed", err);
      funnel.submitError("network", { message: (err as Error)?.message });
      toast({
        title: "Something went wrong",
        description: "Please try again, or call us at (888) 416-3144.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const toneClasses = {
    white: "bg-background border-border",
    offwhite: "bg-offwhite border-border",
    "primary-soft": "bg-primary/5 border-primary/20",
  }[tone];

  if (submitted) {
    return (
      <div
        id={id}
        className={`rounded-2xl border ${toneClasses} p-5 sm:p-6 ${className}`}
      >
        <div className="text-center">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
            <CheckCircle2 className="w-5 h-5 text-primary" />
          </div>
          <p className="font-bold text-foreground mb-1">
            Thanks, {name.split(" ")[0]} — we'll be in touch
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            {getChannelResponseLine(preferText ? "text" : "call")}{" "}
            Want to speed it up?{" "}
            <a href="tel:+18884163144" className="text-primary font-semibold hover:underline">
              (888) 416-3144
            </a>
          </p>
        </div>
        <div className="pt-4 border-t border-border/60 max-w-sm mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-widest text-primary mb-3 text-center">
            What happens next
          </p>
          <WhatHappensNext channel={preferText ? "text" : "call"} />
        </div>
      </div>
    );
  }

  const isHorizontal = variant === "horizontal";

  return (
    <form
      id={id}
      onSubmit={(e) => {
        e.preventDefault();
        void submit(false);
      }}
      className={`rounded-2xl border ${toneClasses} p-4 sm:p-6 ${className}`}
      data-capture-draft
      data-capture-source={`quick-quote:${source}`}
    >
      {(eyebrow || heading || subheading) && (
        <div className={`${compact ? "mb-3" : "mb-4 sm:mb-5"} ${isHorizontal ? "text-left" : "text-center"}`}>
          {eyebrow && !compact && (
            <p className="text-primary font-semibold text-[11px] uppercase tracking-widest mb-1">
              {eyebrow}
            </p>
          )}
          {heading && (
            <h3 className={`font-bold text-foreground ${compact ? "text-lg" : "text-xl sm:text-2xl"}`}>
              {heading}
            </h3>
          )}
          {subheading && (
            <p className="text-sm text-muted-foreground mt-1">{subheading}</p>
          )}
        </div>
      )}

      <div
        className={`grid gap-3 ${
          isHorizontal
            ? showFeet
              ? "grid-cols-1 md:grid-cols-5"
              : "grid-cols-1 md:grid-cols-4"
            : "grid-cols-1"
        }`}
      >
        <div className={`${isHorizontal ? "md:col-span-1" : ""} space-y-1`}>
          <Label htmlFor={`qq-name-${source}`} className="text-xs font-medium">
            Name
          </Label>
          <div className="relative">
            <Input
              id={`qq-name-${source}`}
              type="text"
              autoComplete="name"
              enterKeyHint="next"
              maxLength={100}
              value={name}
              onFocus={() => funnel.fieldFocus("name")}
              onChange={(e) => {
                setName(e.target.value);
                if (touched.name) validateField("name", e.target.value);
              }}
              onBlur={(e) => {
                setTouched((t) => ({ ...t, name: true }));
                validateField("name", e.target.value);
                funnel.fieldComplete("name", e.target.value);
              }}
              placeholder="Jane Doe"
              required
              aria-invalid={showNameError || undefined}
              aria-describedby={showNameError ? `qq-name-${source}-err` : undefined}
              className={`h-11 text-base sm:text-sm pr-9 ${
                showNameError ? "border-destructive focus-visible:ring-destructive/40" : ""
              }`}
            />
            {nameValid && !showNameError && (
              <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" aria-hidden="true" />
            )}
            {showNameError && (
              <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-destructive" aria-hidden="true" />
            )}
          </div>
          {showNameError && (
            <p id={`qq-name-${source}-err`} className="text-xs text-destructive flex items-center gap-1">
              <span aria-hidden="true">•</span> {errors.name}
            </p>
          )}
        </div>
        <div className={`${isHorizontal ? "md:col-span-1" : ""} space-y-1`}>
          <Label htmlFor={`qq-phone-${source}`} className="text-xs font-medium">
            Mobile number
          </Label>
          <div className="relative">
            <Input
              id={`qq-phone-${source}`}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              enterKeyHint={showOptional ? "next" : "send"}
              maxLength={20}
              value={phone}
              onFocus={() => funnel.fieldFocus("phone")}
              onChange={(e) => {
                const formatted = formatUSPhone(e.target.value);
                setPhone(formatted);
                if (touched.phone) validateField("phone", formatted);
              }}
              onBlur={(e) => {
                setTouched((t) => ({ ...t, phone: true }));
                validateField("phone", e.target.value);
                funnel.fieldComplete("phone", e.target.value);
              }}
              placeholder="(555) 123-4567"
              required
              aria-invalid={showPhoneError || undefined}
              aria-describedby={showPhoneError ? `qq-phone-${source}-err` : undefined}
              className={`h-11 text-base sm:text-sm pr-9 ${
                showPhoneError ? "border-destructive focus-visible:ring-destructive/40" : ""
              }`}
            />
            {phoneValid && !showPhoneError && (
              <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" aria-hidden="true" />
            )}
            {showPhoneError && (
              <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-destructive" aria-hidden="true" />
            )}
          </div>
          {showPhoneError ? (
            <p id={`qq-phone-${source}-err`} className="text-xs text-destructive flex items-center gap-1">
              <span aria-hidden="true">•</span> {errors.phone}
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">We'll text first — no robocalls.</p>
          )}
        </div>
        {/* Optional fields: hidden on mobile until "+ Add zip" tapped; always visible md+ in horizontal layout. */}
        <div
          className={`${
            isHorizontal
              ? showOptional
                ? "block md:col-span-1"
                : "hidden md:block md:col-span-1"
              : showOptional
                ? "block"
                : "hidden"
          } space-y-1`}
        >
          <Label htmlFor={`qq-zip-${source}`} className="text-xs font-medium">
            Zip <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Input
            id={`qq-zip-${source}`}
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            enterKeyHint={showFeet ? "next" : "send"}
            maxLength={10}
            value={zip}
            onFocus={() => funnel.fieldFocus("zip")}
            onChange={(e) => setZip(e.target.value)}
            onBlur={(e) => funnel.fieldComplete("zip", e.target.value)}
            placeholder="06880"
            className="h-11 text-base sm:text-sm"
          />
        </div>
        {showFeet && (
          <div
            className={`${
              isHorizontal
                ? showOptional
                  ? "block md:col-span-1"
                  : "hidden md:block md:col-span-1"
                : showOptional
                  ? "block"
                  : "hidden"
            } space-y-1`}
          >
            <Label htmlFor={`qq-feet-${source}`} className="text-xs font-medium">
              Linear feet <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id={`qq-feet-${source}`}
              type="number"
              inputMode="numeric"
              enterKeyHint="send"
              min={0}
              value={feet}
              onFocus={() => funnel.fieldFocus("linear_feet")}
              onChange={(e) => setFeet(e.target.value)}
              onBlur={(e) => funnel.fieldComplete("linear_feet", e.target.value)}
              placeholder="500"
              className="h-11 text-base sm:text-sm"
            />
          </div>
        )}
        <div className={`${isHorizontal ? "md:col-span-1 self-end" : ""}`}>
          <Button type="submit" size="lg" disabled={submitting} className="w-full h-12 sm:h-11 gap-1.5 text-base sm:text-sm">
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Phone className="w-4 h-4" /> Call Me
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile expander for optional fields (mobile-only; desktop md+ always shows them). */}
      {!showOptional && zip === "" && feet === "" && (
        <button
          type="button"
          onClick={() => setShowOptional(true)}
          className={`${isHorizontal ? "md:hidden " : ""}block mx-auto mt-2 text-xs text-primary font-semibold underline underline-offset-2 py-2 px-2 min-h-[36px]`}
        >
          + Add zip{showFeet ? " & linear feet" : ""} for a faster quote
        </button>
      )}

      <div className="mt-3 space-y-2">
        <label
          htmlFor={`qq-sms-consent-${source}`}
          className={`flex items-start gap-2 text-xs leading-snug cursor-pointer rounded-md p-1.5 -mx-1.5 ${
            consentError ? "bg-destructive/5 ring-1 ring-destructive/40" : ""
          }`}
        >
          <input
            id={`qq-sms-consent-${source}`}
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => {
              setSmsConsent(e.target.checked);
              if (e.target.checked) setConsentError(false);
            }}
            aria-invalid={consentError || undefined}
            aria-describedby={consentError ? `qq-sms-consent-${source}-err` : undefined}
            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-input accent-primary"
          />
          <span className="text-muted-foreground">
            I agree to receive a text message from Modern Fence Install at the number
            provided about my quote. Msg &amp; data rates may apply. Reply STOP to opt out.
          </span>
        </label>
        {consentError && (
          <p id={`qq-sms-consent-${source}-err`} className="text-xs text-destructive -mt-1">
            Please check the box above to receive a text from us.
          </p>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <button
            type="button"
            disabled={submitting}
            onClick={() => void submit(true)}
            className="inline-flex items-center justify-center gap-1.5 text-sm text-primary font-semibold py-2 px-2 -mx-2 min-h-[44px] active:opacity-70 sm:hover:underline sm:min-h-0 sm:py-0"
          >
            <MessageCircle className="w-4 h-4" />
            Or text me my quote instead
          </button>
          <p className="text-[11px] text-muted-foreground text-center sm:text-right">
            Avg. response: 47 min.
          </p>
        </div>
        <PreSubmitTrust className="pt-1 border-t border-border/50" />
      </div>
    </form>
  );
};

export default QuickQuoteForm;
